/**
 * Microsoft sign-in (Entra ID through Cognito), 2026-10-19.
 *
 * Each company has its own Microsoft 365 tenant, so each has its own Cognito
 * identity provider (Microsoft-ITA, -BL, -CC, -GDM). We send the browser to
 * Cognito's /oauth2/authorize with idp_identifier = the person's email domain,
 * and Cognito forwards straight to that company's Microsoft sign-in. The
 * Cognito sign-in pages are never shown.
 *
 * Code flow with PKCE (public client, no secret). The tokens that come back are
 * saved where amazon-cognito-identity-js keeps its own, so the rest of the app
 * (getCurrentUser, getIdToken, token refresh, sign-out) works unchanged.
 */
const clientId = import.meta.env.VITE_USER_POOL_CLIENT_ID as string | undefined;
const rawDomain = import.meta.env.VITE_COGNITO_DOMAIN as string | undefined;

const PKCE_KEY = "docvault.ms.pkce";
const RETRY_KEY = "docvault.ms.retried";
export const MS_SIGNIN_KEY = "docvault.signin"; // "microsoft" when the last sign-in used Microsoft

/** Base URL of the Cognito domain ("https://..."; a full http URL is allowed for local tests). */
export function cognitoBase(): string | null {
  if (!rawDomain) return null;
  return /^https?:\/\//.test(rawDomain) ? rawDomain.replace(/\/$/, "") : `https://${rawDomain}`;
}
export const callbackUrl = () => `${window.location.origin}/auth/callback`;

function b64url(bytes: Uint8Array): string {
  let s = "";
  bytes.forEach((b) => { s += String.fromCharCode(b); });
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function randomString(n = 48): string {
  const a = new Uint8Array(n);
  crypto.getRandomValues(a);
  return b64url(a);
}
async function challengeFor(verifier: string): Promise<string> {
  const hash = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return b64url(new Uint8Array(hash));
}

/** The email domain the person typed, or null. */
export function emailDomain(email: string): string | null {
  const m = /^[^@\s]+@([^@\s]+\.[^@\s]+)$/.exec(email.trim().toLowerCase());
  return m ? m[1]! : null;
}

/** Leave for Microsoft. `email` picks the company (its domain). */
export async function startMicrosoftSignIn(email: string): Promise<void> {
  const base = cognitoBase();
  const domain = emailDomain(email);
  if (!base || !clientId) throw new Error("Microsoft sign-in isn't set up yet.");
  if (!domain) throw new Error("Enter your work email first, then choose Sign in with Microsoft.");
  const verifier = randomString(48);
  const state = randomString(16);
  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state, email: email.trim() }));
  const q = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: callbackUrl(),
    scope: "openid email profile aws.cognito.signin.user.admin",
    idp_identifier: domain,
    state,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: "S256",
  });
  window.location.assign(`${base}/oauth2/authorize?${q}`);
}

function jwtPayload(token: string): Record<string, unknown> {
  const part = token.split(".")[1] ?? "";
  const json = atob(part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "="));
  return JSON.parse(decodeURIComponent(Array.from(json, (c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0")).join("")));
}

/** Store tokens the way amazon-cognito-identity-js does, so its session code picks them up. */
function saveSession(t: { id_token: string; access_token: string; refresh_token?: string }) {
  const username = String(jwtPayload(t.access_token).username ?? jwtPayload(t.id_token)["cognito:username"] ?? "");
  if (!username || !clientId) throw new Error("The sign-in answer was incomplete.");
  const prefix = `CognitoIdentityServiceProvider.${clientId}`;
  localStorage.setItem(`${prefix}.${username}.idToken`, t.id_token);
  localStorage.setItem(`${prefix}.${username}.accessToken`, t.access_token);
  if (t.refresh_token) localStorage.setItem(`${prefix}.${username}.refreshToken`, t.refresh_token);
  localStorage.setItem(`${prefix}.${username}.clockDrift`, "0");
  localStorage.setItem(`${prefix}.LastAuthUser`, username);
  localStorage.setItem(MS_SIGNIN_KEY, "microsoft");
}

export type CallbackResult = { ok: true } | { ok: false; message: string } | { retrying: true };

/**
 * Finish the sign-in on /auth/callback: check state, swap the code for tokens.
 * The first Microsoft sign-in for someone who already had a DocVault account
 * links the two; Cognito then reports "Already found an entry" once, and a
 * second trip through Microsoft (silent, they're already signed in) succeeds.
 */
export async function finishMicrosoftSignIn(search: string): Promise<CallbackResult> {
  const q = new URLSearchParams(search);
  const saved = JSON.parse(sessionStorage.getItem(PKCE_KEY) || "null") as { verifier: string; state: string; email: string } | null;
  const err = q.get("error_description") || q.get("error");
  if (err) {
    if (/already found an entry/i.test(err) && saved && !sessionStorage.getItem(RETRY_KEY)) {
      sessionStorage.setItem(RETRY_KEY, "1");
      await startMicrosoftSignIn(saved.email);
      return { retrying: true };
    }
    sessionStorage.removeItem(RETRY_KEY);
    return { ok: false, message: friendlyError(err) };
  }
  const code = q.get("code");
  if (!code || !saved || q.get("state") !== saved.state) {
    return { ok: false, message: "That sign-in link has expired. Try Sign in with Microsoft again." };
  }
  sessionStorage.removeItem(PKCE_KEY);
  sessionStorage.removeItem(RETRY_KEY);
  const res = await fetch(`${cognitoBase()}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ grant_type: "authorization_code", client_id: clientId!, code, redirect_uri: callbackUrl(), code_verifier: saved.verifier }),
  });
  if (!res.ok) return { ok: false, message: "Microsoft sign-in didn't complete. Try again." };
  saveSession(await res.json());
  return { ok: true };
}

function friendlyError(raw: string): string {
  if (/idp|identity provider|identifier/i.test(raw)) {
    return "Microsoft sign-in isn't set up for that email's company. Check the address, or sign in with your password.";
  }
  if (/access_denied|cancel/i.test(raw)) return "Microsoft sign-in was canceled.";
  return `Microsoft sign-in didn't complete: ${raw.replace(/\+/g, " ")}`;
}

/** After the local sign-out, also end the Cognito web session so the next sign-in asks again. */
export function microsoftSignOutUrl(): string | null {
  const base = cognitoBase();
  if (!base || !clientId || localStorage.getItem(MS_SIGNIN_KEY) !== "microsoft") return null;
  localStorage.removeItem(MS_SIGNIN_KEY);
  return `${base}/logout?${new URLSearchParams({ client_id: clientId, logout_uri: `${window.location.origin}/login` })}`;
}
